import React, { useCallback, useState } from "react";
import { Tooltip } from "@mui/material";
import useUIPreferenceStore from "../../stores/useUIPreferenceStore";
import AgentAvatar from "../pages/AgentAvatar";
import useContextAgentLauncherAttention, {
  ContextAgentLauncherBubble,
} from "./useContextAgentLauncherAttention";
import { useContextAgent } from "./ContextAgentContext";
import { CONTEXT_AGENT_SPRITE_SIZE } from "./widgetConstants";
import "./ContextAgentSidebarTab.css";

const PULL_OUT_ANIMATION = "context-agent-tab-pull-out";
const PULL_IN_ANIMATION = "context-agent-tab-pull-in";

/** Left-edge launcher — agent avatar or pixel sprite. */
export default function ContextAgentSidebarTab() {
  const assistantLayout = useUIPreferenceStore((s) => s.assistantLayout);
  const { registration, open, setOpen, toggleOpen, isRegistered } = useContextAgent();
  const [isExiting, setIsExiting] = useState(false);

  const {
    attentionMessage,
    bubbleVisible,
    bubbleExiting,
    handleBubbleClick,
    handleBubbleDismiss,
    handleBubbleAnimationEnd,
  } = useContextAgentLauncherAttention(registration, {
    open: open || isExiting,
    setOpen,
  });

  const handleClick = useCallback(() => {
    if (isExiting) {
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      toggleOpen();
      return;
    }
    setIsExiting(true);
  }, [isExiting, toggleOpen]);

  const handleAnimationEnd = useCallback(
    (event) => {
      if (event.currentTarget !== event.target) {
        return;
      }

      const { animationName } = event;
      if (animationName === PULL_IN_ANIMATION && isExiting) {
        setIsExiting(false);
        toggleOpen();
        return;
      }

      if (animationName === PULL_OUT_ANIMATION && !isExiting) {
        event.currentTarget.classList.remove("context-agent-sidebar-tab-wrap--enter");
      }
    },
    [isExiting, toggleOpen]
  );

  if (assistantLayout !== "sidebar" || !isRegistered || !registration) {
    return null;
  }

  if (open && !isExiting) {
    return null;
  }

  const { agentName, scopeLabel } = registration;
  const tooltip = scopeLabel ? `${agentName} · ${scopeLabel}` : agentName;

  const wrapClassName = isExiting
    ? "context-agent-sidebar-tab-wrap context-agent-sidebar-tab-wrap--exit"
    : "context-agent-sidebar-tab-wrap context-agent-sidebar-tab-wrap--enter";

  return (
    <div
      className={wrapClassName}
      style={{ "--context-agent-sprite-size": `${CONTEXT_AGENT_SPRITE_SIZE}px` }}
      onAnimationEnd={handleAnimationEnd}
    >
      <Tooltip
        title={tooltip}
        placement="right"
        arrow
        disableHoverListener={bubbleVisible}
      >
        <button
          type="button"
          className="context-agent-sidebar-tab"
          aria-label={`Open ${agentName}`}
          aria-pressed={false}
          onClick={handleClick}
          data-testid="context-agent-sidebar-tab"
        >
          <AgentAvatar
            agentId={registration?.launcherAgentId}
            agentName={agentName}
            name={agentName}
            size={CONTEXT_AGENT_SPRITE_SIZE}
          />
        </button>
      </Tooltip>
      <ContextAgentLauncherBubble
        bubbleVisible={bubbleVisible}
        bubbleExiting={bubbleExiting}
        attentionMessage={attentionMessage}
        placement="top"
        onBubbleClick={handleBubbleClick}
        onBubbleDismiss={handleBubbleDismiss}
        onBubbleAnimationEnd={handleBubbleAnimationEnd}
      />
    </div>
  );
}
